import { useState } from 'react';
import { MessageCircle, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@solid/ui';
import type { DynamicQuestion } from '@/types';

interface ClarifyingStepProps {
  questions: DynamicQuestion[];
  onSubmit: (answers: Record<string, string>) => void;
  isSubmitting?: boolean;
}

export default function ClarifyingStep({
  questions,
  onSubmit,
  isSubmitting,
}: ClarifyingStepProps) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    questions.forEach((_, i) => {
      initial[`q${i}`] = '';
    });
    return initial;
  });

  const handleSubmit = () => {
    const questionAnswers: Record<string, string> = {};
    questions.forEach((q, i) => {
      if (answers[`q${i}`]?.trim()) {
        questionAnswers[q.question] = answers[`q${i}`].trim();
      }
    });
    onSubmit(questionAnswers);
  };

  const hasAnswers = Object.values(answers).some((a) => a.trim());

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-500/20 mb-4">
          <MessageCircle className="w-6 h-6 text-accent-400" />
        </div>
        <h2 className="text-xl font-semibold text-white">
          Just a couple quick questions
        </h2>
        <p className="text-slate-400 mt-1">
          This helps us prepare for our conversation and connect you with the
          right person
        </p>
      </div>

      <div className="space-y-6 mt-8">
        {questions.map((q, i) => (
          <div key={i} className="space-y-2">
            <label className="block text-sm font-medium text-white">
              {q.question}
              {q.priority === 'high' && (
                <span className="text-red-400 ml-1">*</span>
              )}
            </label>
            {q.reason && (
              <p className="text-xs text-slate-400 flex items-start gap-1">
                <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {q.reason}
              </p>
            )}
            <textarea
              value={answers[`q${i}`]}
              onChange={(e) =>
                setAnswers((prev) => ({ ...prev, [`q${i}`]: e.target.value }))
              }
              placeholder="Your answer..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 resize-none"
              rows={3}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!hasAnswers}
          className="gap-2 bg-accent-600 hover:bg-accent-700 text-white"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-xs text-slate-500 text-center">
        All fields are optional, but more information helps us serve you better
      </p>
    </div>
  );
}
